import CryptoJS from "crypto-js";
import Utils from "@/project/Source/utils.js";
import { IS_POLICY_PROD } from "../Reports/agentRequestsApi.js";
import { resolvePremiumCalculated, resolveSumInsuredCalculated, productAdminChargeNumber } from "./catalogProductDisplay.js";

const CRYPT_KEY = "secret_key";

/** Fallback only if product has no series field. */
export const AGENT_REQUEST_POLICY_SERIES = "TSA";

/** policySeries for AgentRequestInsert — from product row (API Product). */
export function pickPolicySeriesFromProduct(product) {
  if (!product || typeof product !== "object") return "";
  const keys = [
    "policySeries",
    "PolicySeries",
    "policySerie",
    "PolicySerie",
    "serie",
    "Serie",
    "series",
    "Series",
    "serieName",
    "SerieName",
  ];
  for (const key of keys) {
    const v = product[key];
    if (v != null && String(v).trim() !== "") return String(v).trim();
  }
  return "";
}

const POSTAL_INDEX_RE = /^AZ[0-9]{4}$/i;

export function getSessionUserId() {
  try {
    const raw = window.localStorage.getItem("user");
    if (!raw) return 0;
    const user = JSON.parse(CryptoJS.AES.decrypt(raw, CRYPT_KEY).toString(CryptoJS.enc.Utf8));
    return user?.userId ?? user?.id ?? 0;
  } catch {
    return 0;
  }
}

export function normalizePin(pin) {
  return pin != null ? String(pin).trim().toUpperCase() : "";
}

function nonEmptyString(value) {
  if (value == null) return null;
  const s = String(value).trim();
  return s === "" ? null : s;
}

/** Omit null/undefined and empty strings (API rejects "" with Size = 0). */
function compactPayload(obj) {
  if (!obj || typeof obj !== "object") return obj;
  const out = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value === null || value === undefined) continue;
    if (typeof value === "string" && value.trim() === "") continue;
    out[key] = value;
  }
  return out;
}

function pickAddressString(values) {
  if (!values || typeof values !== "object") return null;
  for (const key of ["address", "customAddress", "street"]) {
    const v = values[key];
    if (v != null && String(v).trim() !== "") return String(v).trim();
  }
  return null;
}

/** Single line for AgentRequest.address / AgentRequestCard.address */
export function buildAddressForAgentRequest(values) {
  return pickAddressString(values);
}

/** postalIndex for AgentRequestCard (form field `zip` or API-shaped keys). */
export function buildPostalIndexForAgentRequest(values) {
  if (!values || typeof values !== "object") return null;
  for (const key of ["postalIndex", "PostalIndex", "zip", "postalCode", "PostalCode"]) {
    const v = values[key];
    if (v == null || String(v).trim() === "") continue;
    let z = String(v).trim().toUpperCase();
    if (!/^AZ/.test(z)) z = `AZ${z}`;
    if (!POSTAL_INDEX_RE.test(z)) continue;
    return z;
  }
  return null;
}

function formatDobForAgentRequest(dateOfBirth) {
  if (!dateOfBirth) return null;
  const d = Utils.DateFormat(dateOfBirth);
  if (!d || Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function resolvePersonPin(values, searchedPin) {
  return searchedPin != null && searchedPin !== ""
    ? normalizePin(searchedPin)
    : normalizePin(values?.pin);
}

/** Identity card series for AgentRequestInsert — API field IdSeries (e.g. AZE). */
export function resolveIdSeries(values) {
  const raw =
    values?.docSeries ?? values?.idSeries ?? values?.IdSeries ?? values?.policySeries;
  if (raw == null) return null;
  const s = String(raw).trim().toUpperCase();
  return s || null;
}

/** Identity card number for AgentRequestInsert — API field IdNumber. */
export function resolveIdNumber(values) {
  const raw = values?.docNumber ?? values?.idNumber ?? values?.IdNumber;
  if (raw == null) return null;
  const s = String(raw).trim();
  return s || null;
}

/** Manual entries: PIN / IdSeries / IdNumber optional; address + email always required. */
export function validateAgentRequestPersonValues(values) {
  if (!buildAddressForAgentRequest(values)) return "catalogAddressRequired";
  const email = values?.email != null ? String(values.email).trim() : "";
  if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return "catalogEmailRequired";
  return "";
}

/** Person fields shared by data (sığortalı) and cards (sığorta olunanlar). */
export function buildAgentRequestPersonFields(values, options = {}) {
  const pin = resolvePersonPin(values, options.searchedPin);

  return compactPayload({
    pin: nonEmptyString(pin),
    IdSeries: resolveIdSeries(values),
    IdNumber: resolveIdNumber(values),
    firstName: nonEmptyString(values?.firstName),
    lastName: nonEmptyString(values?.lastName),
    middleName: nonEmptyString(values?.middleName),
    sex: nonEmptyString(values?.sex),
    dob: formatDobForAgentRequest(values?.dateOfBirth),
    address: buildAddressForAgentRequest(values),
    phoneMob: nonEmptyString(values?.mobilePhone ?? values?.phoneMob),
    email: nonEmptyString(values?.email),
    isAsan: options.isAsan === true,
  });
}

/** One insured person in AgentRequest.cards[] */
export function buildAgentRequestCardItem(values, options = {}) {
  const postalIndex = buildPostalIndexForAgentRequest(values);

  return compactPayload({
    ...buildAgentRequestPersonFields(values, options),
    postalIndex,
    commandUserId: getSessionUserId(),
  });
}

/** Total premium for AgentRequestInsert — IsStatic keeps unit premium, else unit × insured count. */
export { resolvePremiumCalculated, resolveSumInsuredCalculated } from "./catalogProductDisplay.js";

/**
 * AgentRequestInsert body:
 * - `data` (outside cards) — main sığortalı from the top form
 * - `data.cards[]` — additional sığorta olunanlar from the queue
 */
export function buildAgentRequestInsertPayload(
  productId,
  queuedPersons = [],
  formValues = {},
  options = {}
) {
  const mainFields = buildAgentRequestPersonFields(formValues, {
    searchedPin: options.mainSearchedPin,
    isAsan: options.mainIsAsan === true,
  });
  const mainPostalIndex = buildPostalIndexForAgentRequest(formValues);

  const cards = (queuedPersons || [])
    .map((person) =>
      buildAgentRequestCardItem(person.values, {
        searchedPin: person.searchedPin,
        isAsan: person.isAsan === true,
      })
    )
    .filter((card) => card.firstName);

  const mainValidation = validateAgentRequestPersonValues(formValues);
  if (mainValidation) {
    throw new Error(mainValidation);
  }
  for (const person of queuedPersons || []) {
    const cardValidation = validateAgentRequestPersonValues(person?.values);
    if (cardValidation) {
      throw new Error(cardValidation);
    }
  }

  const mainPin = mainFields.pin || normalizePin(formValues?.pin);
  const idxPin = mainPin || cards[0]?.pin || "1";
  const idxChar = idxPin ? String(idxPin).charAt(0) : "1";
  const policySeries =
    nonEmptyString(options.policySeries) ||
    pickPolicySeriesFromProduct(options.product) ||
    AGENT_REQUEST_POLICY_SERIES;

  // Insured count matches insert UI: cards list; fallback to 1 when only main is present.
  const insuredCount =
    options.insuredCount != null ? Number(options.insuredCount) : Math.max(cards.length, 1);
  const premiumCalculated =
    options.premiumCalculated != null
      ? Number(options.premiumCalculated)
      : resolvePremiumCalculated(options.product, insuredCount);
  const sumInsuredCalculated =
    options.sumInsuredCalculated != null
      ? Number(options.sumInsuredCalculated)
      : resolveSumInsuredCalculated(options.product, insuredCount);
  const deductible =
    options.deductible != null
      ? Number(options.deductible)
      : productAdminChargeNumber(options.product);

  return {
    requestCorrelationId: crypto.randomUUID(),
    data: compactPayload({
      productId: Number(productId),
      policySeries,
      idx: idxChar || "1",
      ...mainFields,
      ...(mainPostalIndex ? { postalIndex: mainPostalIndex } : {}),
      premium:
        premiumCalculated != null && Number.isFinite(Number(premiumCalculated))
          ? Number(premiumCalculated)
          : undefined,
      sumInsured:
        sumInsuredCalculated != null && Number.isFinite(Number(sumInsuredCalculated))
          ? Number(sumInsuredCalculated)
          : undefined,
      deductible:
        deductible != null && Number.isFinite(Number(deductible)) ? Number(deductible) : undefined,
      commandUserId: getSessionUserId(),
      cards,
    }),
  };
}

/** @deprecated Use buildAgentRequestInsertPayload for catalog batch submit */
export function buildAgentRequestPayload(productId, values, towns = [], options = {}) {
  void towns;
  return buildAgentRequestInsertPayload(productId, [], values, {
    mainSearchedPin: options.searchedPin,
  });
}

/** reqId from AgentRequestInsert — API field `id` in NewAgentRequestResponse. */
export function parseAgentRequestInsertResult(response) {
  const body = response?.data;
  if (!body || body.isSuccess === false) return null;
  const raw = body.reqId ?? body.id ?? body.data?.reqId ?? body.data?.id;
  if (raw == null || String(raw).trim() === "") return null;
  return { reqId: String(raw).trim() };
}

/** Route to PROD policy detail after catalog insert. */
export function buildProdDetailRouteAfterInsert(productId, reqId) {
  const pid = productId != null ? String(productId).trim() : "";
  const rid = reqId != null ? String(reqId).trim() : "";
  if (!pid || !rid) return null;
  return {
    name: "PolicyDetail",
    params: { id: pid },
    query: { type: IS_POLICY_PROD, reqId: rid },
    state: {
      agentListRow: {
        isPolicyKind: IS_POLICY_PROD,
        isPolicyRaw: IS_POLICY_PROD,
        policyId: pid,
        productId: pid,
        reqId: rid,
      },
    },
  };
}
